import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { MessageSquare } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import ServiceChat from '../../components/services/ServiceChat';

const SERVICE_TYPE_LABELS = {
  PROFILE_ASSESSMENT:       'Profile Assessment',
  UNIVERSITY_SHORTLISTING:  'Preapplication Support',
  APPLICATION_ASSISTANCE:   'Apply University',
  VISA_GUIDANCE:            'Visa & Interview Support',
  SCHOLARSHIP_SEARCH:       'Ticket & Travel Support',
  LOAN_ASSISTANCE:          'Education Loan',
  ACCOMMODATION_HELP:       'Find Accommodation',
  PRE_DEPARTURE_ORIENTATION:'Find Jobs Abroad',
};

const STATUS_DOT = {
  IN_PROGRESS: 'bg-amber-400',
  ASSIGNED:    'bg-blue-400',
  COMPLETED:   'bg-emerald-400',
  CANCELLED:   'bg-gray-300',
};

export default function PartnerMessages() {
  const [serviceRequests, setServiceRequests] = useState([]);
  const [selectedSr, setSelectedSr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    partnerAPI.getServiceRequests({ limit: 100 })
      .then(res => {
        const list = res.data || [];
        setServiceRequests(list);
        if (list.length > 0) setSelectedSr(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PartnerLayout>
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Messages</h1>
          <p className="mt-1 text-sm text-slate-600">
            Chat with counselors about your students' service requests
          </p>
        </div>

        {loading ? (
          <div className="flex gap-4 h-[600px]">
            <div className="partner-card w-72 flex-shrink-0 space-y-3 rounded-[26px] p-4 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 rounded-2xl bg-white/45" />
              ))}
            </div>
            <div className="partner-card flex-1 rounded-[26px] animate-pulse" />
          </div>
        ) : serviceRequests.length === 0 ? (
          <div className="partner-card rounded-[28px] py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#A87DFA]/16 bg-white/45">
              <MessageSquare className="w-7 h-7 text-[#A87DFA]" />
            </div>
            <h3 className="mb-1 font-semibold text-slate-900">No messages yet</h3>
            <p className="mx-auto max-w-sm text-sm text-slate-600">
              Conversations will appear here once you create service requests for your students
            </p>
          </div>
        ) : (
          <div className="flex gap-4" style={{ height: 'calc(100vh - 240px)', minHeight: '480px' }}>
            {/* Left: conversation list */}
            <div className="partner-card flex w-72 flex-shrink-0 flex-col overflow-hidden rounded-[26px]">
              <div className="border-b border-[#A87DFA]/14 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {serviceRequests.length} Conversation{serviceRequests.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="partner-scrollbar flex-1 divide-y divide-[#A87DFA]/10 overflow-y-auto">
                {serviceRequests.map(sr => {
                  const label = SERVICE_TYPE_LABELS[sr.serviceType] || sr.serviceType || 'Service';
                  const counselorName = sr.counselor
                    ? `${sr.counselor.firstName || ''} ${sr.counselor.lastName || ''}`.trim()
                    : 'Awaiting Assignment';
                  const studentName = sr.student?.user
                    ? `${sr.student.user.firstName || ''} ${sr.student.user.lastName || ''}`.trim()
                    : '';
                  const isSelected = selectedSr?.serviceRequestId === sr.serviceRequestId;
                  const dotColor = STATUS_DOT[sr.status] || 'bg-gray-300';

                  return (
                    <button
                      key={sr.serviceRequestId}
                      onClick={() => setSelectedSr(sr)}
                      className={`relative w-full px-4 py-3.5 text-left transition-colors ${
                        isSelected ? 'bg-[#A87DFA]/10' : 'hover:bg-white/40'
                      }`}
                    >
                      {isSelected && (
                        <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-purple-500 rounded-r-full" />
                      )}
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                        <p className={`text-sm font-medium truncate ${
                          isSelected ? 'text-[#6D49D4]' : 'text-slate-800'
                        }`}>
                          {label}
                        </p>
                      </div>
                      {studentName && (
                        <p className="truncate pl-3.5 text-xs text-slate-500">{studentName}</p>
                      )}
                      <p className="truncate pl-3.5 text-xs text-slate-500">{counselorName}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: chat panel (Rep3 can send messages) */}
            <div className="partner-card flex-1 overflow-hidden rounded-[26px]">
              {selectedSr ? (
                <ServiceChat
                  serviceRequestId={selectedSr.serviceRequestId}
                  counselor={selectedSr.counselor}
                  isStaffView={false}
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-500">
                  <MessageSquare className="w-8 h-8 text-[#CDB8F9]" />
                  <p className="text-sm">Select a conversation to start messaging</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PartnerLayout>
  );
}
